import * as Sentry from "@sentry/react";

const REDACTED = "***REDACTED***";
const PSEUDONYMIZED = "***PSEUDONYMIZED***";
const GENERALIZED = "***GENERALIZED***";
const secretKey =
  /(password|passwd|token|secret|authorization|api[_-]?key|credential)/i;
const identityKey = /(email|username|user_id|actor|claim|idp|sub)/i;
const externalIdKey =
  /(device_id|eui|external_id|imei|mac|platform_device_id|serial|tenant_id)/i;
const locationKey =
  /(address|coordinates|geojson|geometry|latitude|longitude|position|(^|_)lat($|_)|(^|_)(lng|lon)($|_))/i;
const fileReferenceKey = /(file|path|presigned|signed_url|uri|url)/i;
const inlineSecret =
  /\b(password|passwd|token|secret|authorization|api[_-]?key)(\s*[:=]\s*)([^\s,;]+)/gi;

export function sanitizeTelemetry(value: unknown, parentKey = ""): unknown {
  if (secretKey.test(parentKey)) return REDACTED;
  if (identityKey.test(parentKey)) return PSEUDONYMIZED;
  if (externalIdKey.test(parentKey)) return PSEUDONYMIZED;
  if (locationKey.test(parentKey)) return GENERALIZED;
  if (fileReferenceKey.test(parentKey)) return REDACTED;
  if (Array.isArray(value))
    return value.map((item) => sanitizeTelemetry(item, parentKey));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        sanitizeTelemetry(item, key),
      ]),
    );
  }
  if (typeof value === "string") {
    return value.replace(inlineSecret, (_match, key, separator) => {
      return `${key}${separator}${REDACTED}`;
    });
  }
  return value;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const replaysSessionSampleRate = Number(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? "0",
  );
  const replaysOnErrorSampleRate = Number(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? "0",
  );
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "development",
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.1",
    ),
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    sendDefaultPii: false,
    beforeSend: (event) => sanitizeTelemetry(event) as typeof event,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(replaysSessionSampleRate > 0 || replaysOnErrorSampleRate > 0
        ? [Sentry.replayIntegration()]
        : []),
    ],
  });
}
